import { useEffect } from 'react'
import { ArticleJsonLd } from 'next-seo'
import ArticleSplitHero from '@/components/Article/ArticleSplitHero'
import ArticleMain from '@/components/Article/ArticleMain'
import ContentSections from '@/components/Sections/ContentSections'
import StructuredData from '@/components/SEO/StructuredData'
import PageSEO from '@/components/SEO/PageSEO'
import { getRecentArticlesHandles } from '@/utils/getRecentArticleHandles'
import { useModalContext } from '@/context/ModalContext'
import { useCustomerContext } from '@/context/CustomerContext'
import {
  getContentGatingModal,
  userCanAccessContent,
} from '@/utils/gatingUtils'
import { getGatedArticleModal } from '@/utils/getGatedArticleModal'
import { getProductsByHandles } from '@/utils/getProducts'
import { getStandardArticleByHandle } from '@/utils/getArticles'
import { getSanityClient } from '@/services/sanityClient'
import { getBlogByHandle, getBlogSettings } from '@/utils/getBlogs'
import groq from 'groq'
import { PreviewButton } from '@/components/Preview/PreviewButton'
import { DateTime } from 'luxon'

const BrandArticle = ({
  page,
  blog,
  products,
  blogSettings,
  modals,
  preview,
  previewToken,
}) => {
  const { hero, articleTags } = page.fields
  const contentTags = articleTags?.map((t) => t.value)
  const blogType = page.fields.blog?.blogType
  const blogGlobalSettings = blogSettings
    ? { ...blogSettings.fields[blogType], blogType }
    : undefined
  hero.header = page.title
  hero.subheader = page.fields.subheader

  const modalContext = useModalContext()
  const customerContext = useCustomerContext()
  const { customer } = customerContext

  // Add articleUrl definition for SEO
  const articleUrl = `${process.env.NEXT_PUBLIC_DOMAIN}/blogs/stories/${blog.handle.current}/${page.handle.current}`

  // Format dates for structured data
  const datePublished = DateTime.fromISO(page.fields?.publishedDate || page.createdAt).toISO()
  const dateModified = DateTime.fromISO(page.updatedAt).toISO()

  // GATING
  useEffect(() => {
    const customerHasAccess = userCanAccessContent({
      contentTags,
      userTags: customer?.tags,
    })

    if (!customerHasAccess) {
      const foundModal = getContentGatingModal({ modals, contentTags })
      if (foundModal) {
        modalContext.setContent(foundModal.fields)
        modalContext.setModalType('gated_product')
        modalContext.setIsOpen(true)
      }
    }

    if (modalContext.modalType === 'gated_product' && customerHasAccess) {
      modalContext.setIsOpen(false)
    }
  }, [customer, modalContext.isOpen])

  return (
    <>
      {preview && previewToken && <PreviewButton />}
      <StructuredData type="article" data={page} />
      <PageSEO
        seo={page.fields.seo}
        images={[{
          url: page.fields?.hero?.desktopBackgroundImage?.asset?.url,
          alt: page.fields?.hero?.header || page.title || ''
        }].filter(image => image.url)}
      />
      <ArticleJsonLd
        type="Article"
        url={articleUrl}
        title={page.fields?.seo?.metaTitle || page.title}
        images={[page.fields?.hero?.desktopBackgroundImage?.asset?.url].filter(Boolean)}
        description={page.fields?.seo?.metaDesc || ''}
        datePublished={datePublished}
        dateModified={dateModified}
        authorName={page.fields?.sidebar?.author?.name || 'Sitka Salmon Shares'}
        publisherName="Sitka Salmon Shares"
        publisherLogo={blogSettings?.logo?.asset?.url || ''}
        isAccessibleForFree={true}
      />
      <ArticleSplitHero
        fields={hero}
        blog={blog}
        renderType="default"
        blogGlobalSettings={blogGlobalSettings}
      />
      <ArticleMain
        contentType="standard"
        datePublished={datePublished}
        fields={page.fields}
        products={products}
        blogGlobalSettings={blogGlobalSettings}
      />
      <ContentSections sections={page.fields.pageContent} />
    </>
  )
}

export default BrandArticle

export async function getStaticPaths() {
  const standardArticles = await getSanityClient().fetch(
    groq`*[_type == "standardArticle"]{ handle { current }, blog->{ blogType, handle { current } } }`,
  )

  const handles = standardArticles
    .filter((article) => article.blog.blogType === 'stories')
    .map((article) => {
      return {
        params: {
          handle: article.handle.current,
          category: article.blog.handle.current,
        },
      }
    })

  return {
    paths: handles,
    fallback: 'blocking',
  }
}

export async function getStaticProps({ params, draftMode }) {
  const preview = draftMode || false
  const previewToken = preview ? process.env.SANITY_READ_TOKEN : ``
  if (preview && !previewToken) {
    throw new Error(
      `Preview mode is active, but SANITY_READ_TOKEN is not set in environment variables`,
    )
  }

  const sanityClient = getSanityClient(previewToken)

  const page = await getStandardArticleByHandle(params.handle, sanityClient)

  if (!page || !page.fields.published) {
    return {
      notFound: true,
    }
  }

  const [blog, blogSettings, gatedArticleModal] = await Promise.all([
    getBlogByHandle(params.category, sanityClient),
    getBlogSettings(sanityClient),
    getGatedArticleModal(sanityClient),
  ])

  const props = {
    page: page,
    handle: page.handle,
    blogSettings: blogSettings,
    modals: gatedArticleModal,
    blog,
    preview,
    previewToken,
  }

  if (page.fields?.content) {
    const handles = page.fields.content
      .filter((item) => item._type === 'productBlock')
      .map((item) => item.product)
    if (handles.length) {
      const products = await getProductsByHandles({ productHandles: handles })
      if (products && products.length) {
        props.products = products
      }
    }
  }

  if (
    page?.fields?.pageContent?.some(
      (content) => content._type === 'featuredBlogContent',
    )
  ) {
    await getRecentArticlesHandles(page.fields.pageContent)
  }

  return {
    props,
  }
}
